/**
 * BundleLoader — runtime reader for cooked .vpak asset bundles.
 *
 * Opens a `.vpak` produced by `tools/cooker`, reads its index, and serves the
 * contained files as `ArrayBuffer`s or object URLs. Designed for the browser
 * (HTTP Range fetches, Web Crypto, DecompressionStream) but works anywhere the
 * Fetch + Web Streams APIs exist (Node 18+ included, which is how the tests run).
 *
 * Fetch strategy:
 *  - **Lazy (default):** fetch the 32-byte header via `Range: bytes=0-31`, then
 *    the index via a second ranged fetch, then range-fetch each blob on first
 *    `get()`. Requires the server to honor Range (responds 206). If the server
 *    returns 200 (full body) for the header request, we transparently fall back
 *    to buffering the whole pack from that already-downloaded body.
 *  - **Eager (`{ eager: true }`):** fetch the entire pack once up front and slice
 *    blobs out of memory. Best for small packs or when Range is unavailable.
 *
 * Decompression: brotli blobs use `DecompressionStream('br')`. Safari has not
 * shipped the `'br'` format; we detect this and throw a clear, actionable error
 * naming the offending path so callers can cook with `--brotli` disabled (or
 * serve `Content-Encoding: br` at the HTTP layer instead).
 *
 * Integration with the rest of the engine:
 *  - `mountToManager(manager)` registers a `THREE.LoadingManager.setURLModifier`
 *    that rewrites `vpak://<mount>/<path>` (and any registered prefix) to a
 *    cached object URL. This is the seam every THREE loader already calls via
 *    `FileLoader → manager.resolveURL(url)`, so `gltf.load('vpak://…')` works
 *    with zero changes to GLTFPipeline. Because `setURLModifier` is synchronous,
 *    you must `prewarm()` (or open `eager`) the needed paths first — see docs.
 *  - `resolveUrl(url)` is a standalone synchronous resolver returning a cached
 *    object URL (or the original URL if it isn't a vpak URL / not yet warmed).
 *  - `mountToRegistry(registry)` is a thin convenience that records the loader so
 *    application code can look it up; it does not monkey-patch the registry.
 */

import { Logger } from '../../core/Logger.js';
import {
  HEADER_SIZE,
  HEADER_FLAG_INDEX_BROTLI,
  BLOB_FLAG_BROTLI,
  decodeHeader,
  type VpakIndex,
  type VpakBlob,
  type VpakEntry,
} from './VpakFormat.js';

/** Default number of blob fetches prewarm() keeps in flight (HTTP/1.1 browsers cap at 6/host anyway). */
const PREWARM_CONCURRENCY = 8;

/** A minimal structural type for THREE.LoadingManager (avoids a hard import). */
export interface UrlModifierManager {
  setURLModifier(transform: (url: string) => string): unknown;
}

/** A minimal structural type for AssetRegistry (avoids a hard import / cycle). */
export interface RegistryLike {
  // intentionally empty — we only need object identity for the registry map
  readonly loadedCount?: number;
}

export interface BundleOpenOptions {
  /** Fetch the whole pack up front instead of range-fetching per blob. */
  eager?: boolean;
  /**
   * Verify each blob's SHA-256 against the index after fetching. Off by default
   * (hashing every blob has a cost); turn on for untrusted CDNs or QA builds.
   */
  verify?: boolean;
  /**
   * Custom fetch implementation (tests inject one). Defaults to global `fetch`.
   */
  fetchImpl?: typeof fetch;
  /**
   * Mount name used to build `vpak://<mount>/<path>` URLs. Defaults to a name
   * derived from the pack URL's filename (without extension).
   */
  mount?: string;
}

export interface BundleStats {
  mount: string;
  url: string;
  /** Number of logical files (paths). */
  fileCount: number;
  /** Number of physical blobs after dedup. */
  blobCount: number;
  /** Sum of original (uncompressed) byte lengths across unique blobs. */
  uncompressedBytes: number;
  /** Sum of stored byte lengths across unique blobs. */
  storedBytes: number;
  /** Logical bytes addressed by all paths (counts dedup'd blobs once per path). */
  logicalBytes: number;
  /** True when the whole pack is buffered in memory (eager or Range fallback). */
  buffered: boolean;
}

export class BundleLoader {
  readonly url: string;
  readonly mount: string;

  private readonly fetchImpl: typeof fetch;
  private readonly verifyDefault: boolean;

  private index!: VpakIndex;
  /** path → entry, for O(1) lookups. */
  private byPath = new Map<string, VpakEntry>();

  /** Whole-pack bytes when buffered (eager or Range-unsupported fallback). */
  private packBytes: Uint8Array | null = null;

  /** Decoded (uncompressed, verified) blob bytes, keyed by blob index. */
  private blobCache = new Map<number, Promise<Uint8Array>>();
  /** path → object URL, created on demand by getBlobUrl / prewarm. */
  private objectUrls = new Map<string, string>();

  private disposed = false;

  private constructor(url: string, mount: string, fetchImpl: typeof fetch, verify: boolean) {
    this.url = url;
    this.mount = mount;
    this.fetchImpl = fetchImpl;
    this.verifyDefault = verify;
  }

  /**
   * Open a .vpak bundle. Reads the header + index immediately; blob bytes are
   * fetched lazily unless `eager` is set.
   */
  static async open(url: string, opts: BundleOpenOptions = {}): Promise<BundleLoader> {
    // WHY the wrapper: the loader calls `this.fetchImpl(...)`, i.e. with the
    // loader as `this`. Browsers' window.fetch throws "Illegal invocation" when
    // invoked on anything but the window (Node's fetch does not check, which is
    // why every unit test passed while every pack open in Chrome failed —
    // Atlas asset packs, 2026-09-02). Bind it to the global.
    const fetchImpl: typeof fetch = opts.fetchImpl ?? ((input, init) => globalThis.fetch(input, init));
    if (typeof fetchImpl !== 'function') {
      throw new Error('BundleLoader.open: no fetch implementation available (pass opts.fetchImpl)');
    }
    const mount = opts.mount ?? deriveMount(url);
    const loader = new BundleLoader(url, mount, fetchImpl, opts.verify ?? false);

    if (opts.eager) {
      await loader._loadEager();
    } else {
      await loader._loadLazy();
    }
    Logger.info(
      'Bundle',
      `Opened "${url}" as mount "${mount}" (${loader.index.entries.length} files, ` +
        `${loader.index.blobs.length} blobs${loader.packBytes ? ', buffered' : ', ranged'})`,
    );
    return loader;
  }

  // ── Public query API ─────────────────────────────────────────────────────

  /** List all logical file paths in the bundle (sorted). */
  list(): string[] {
    return this.index.entries.map((e) => e.path);
  }

  /** Whether the bundle contains `path`. */
  has(path: string): boolean {
    return this.byPath.has(normalizePath(path));
  }

  /** Aggregate stats for diagnostics / HUDs. */
  stats(): BundleStats {
    let uncompressedBytes = 0;
    let storedBytes = 0;
    for (const b of this.index.blobs) {
      uncompressedBytes += b.length;
      storedBytes += b.stored;
    }
    let logicalBytes = 0;
    for (const e of this.index.entries) {
      logicalBytes += this.index.blobs[e.blob]?.length ?? 0;
    }
    return {
      mount: this.mount,
      url: this.url,
      fileCount: this.index.entries.length,
      blobCount: this.index.blobs.length,
      uncompressedBytes,
      storedBytes,
      logicalBytes,
      buffered: this.packBytes !== null,
    };
  }

  /** The MIME type recorded for `path`, or undefined. */
  contentType(path: string): string | undefined {
    return this.byPath.get(normalizePath(path))?.contentType;
  }

  /**
   * Get the decompressed bytes for `path` as an ArrayBuffer. The returned buffer
   * is a fresh copy owned by the caller (safe to transfer / mutate).
   *
   * @param opts.verify — override the bundle-level verify flag for this call.
   *   Verification runs once, when the blob is first fetched + decoded; a cached
   *   blob is returned as-is. Open the bundle with `{ verify: true }` to verify
   *   every blob on its initial fetch.
   */
  async get(path: string, opts?: { verify?: boolean }): Promise<ArrayBuffer> {
    this._assertLive();
    const norm = normalizePath(path);
    const entry = this.byPath.get(norm);
    if (!entry) throw new Error(`BundleLoader: path not found: "${path}" (mount "${this.mount}")`);
    const bytes = await this._blob(entry.blob, opts?.verify);
    // Return a copy so callers can't corrupt the cache, and so the ArrayBuffer
    // is exactly sized (the cache may hold a view into a larger buffer).
    return bytes.slice().buffer;
  }

  /**
   * Get an object URL (`blob:`) for `path`, tagged with its content type. The
   * URL is cached and reused; all URLs are revoked on `dispose()`.
   *
   * Use this to feed THREE loaders synchronously after warming, or to set
   * `<img src>` / `<audio src>` directly.
   */
  async getBlobUrl(path: string): Promise<string> {
    this._assertLive();
    const norm = normalizePath(path);
    const existing = this.objectUrls.get(norm);
    if (existing) return existing;

    const entry = this.byPath.get(norm);
    if (!entry) throw new Error(`BundleLoader: path not found: "${path}" (mount "${this.mount}")`);
    const bytes = await this._blob(entry.blob);
    const type = entry.contentType ?? 'application/octet-stream';
    // The Blob constructor snapshots (copies) BufferSource parts per the File
    // API spec, so it never aliases the cache — the previous explicit
    // `bytes.slice()` was a second full copy of every warmed asset.
    const blob = new Blob([bytes as Uint8Array<ArrayBuffer>], { type });
    const objUrl = createObjectURL(blob);
    // dispose() may have run during the await — its sweep already cleared the
    // cache, so this fresh URL would leak (never revoked) and re-populate a dead
    // map. Revoke it and surface the same error _assertLive() throws.
    if (this.disposed) {
      revokeObjectURL(objUrl);
      throw new Error('BundleLoader: used after dispose()');
    }
    // A concurrent call may have raced us; honor the first winner.
    const won = this.objectUrls.get(norm);
    if (won) {
      revokeObjectURL(objUrl);
      return won;
    }
    this.objectUrls.set(norm, objUrl);
    return objUrl;
  }

  /**
   * Pre-create object URLs for a set of paths (or all paths if omitted). After
   * this resolves, `resolveUrl()` / the LoadingManager URL modifier can serve
   * those paths synchronously — required for THREE's synchronous resolveURL.
   */
  async prewarm(paths?: Iterable<string>, opts?: { concurrency?: number }): Promise<void> {
    this._assertLive();
    const list = paths ? [...paths] : this.list();
    // Bounded fan-out: `Promise.all(list.map(getBlobUrl))` fired one Range
    // request per path at once — a thundering herd of hundreds of concurrent
    // fetches for a real pack, each queued response body resident at the same
    // time. A small worker pool reaches the identical warmed state (same
    // paths, same object URLs; the first failure still rejects) with at most
    // `concurrency` blob fetches in flight.
    const limit = Math.max(1, Math.min(opts?.concurrency ?? PREWARM_CONCURRENCY, list.length));
    let next = 0;
    // First failure wins, but the pool drains before we reject: a bare
    // Promise.all would settle on the first error while sibling workers were
    // still in flight, and their later rejections (e.g. after the caller
    // disposes the loader) would surface as UNHANDLED rejections.
    let failed = false;
    let firstErr: unknown;
    const worker = async (): Promise<void> => {
      while (next < list.length && !failed) {
        const p = list[next++];
        try { await this.getBlobUrl(p); }
        catch (e) { if (!failed) { failed = true; firstErr = e; } }
      }
    };
    const workers: Promise<void>[] = [];
    for (let i = 0; i < limit; i++) workers.push(worker());
    await Promise.all(workers);
    if (failed) throw firstErr;
  }

  // ── Integration seams ─────────────────────────────────────────────────────

  /**
   * Synchronously resolve a URL to a cached object URL if it addresses this
   * bundle and has been warmed; otherwise return the input unchanged.
   *
   * Recognized input forms:
   *   - `vpak://<mount>/<path>`
   *   - `<path>` already present in the bundle (bare relative path)
   *
   * NOTE: only returns a blob URL for paths already warmed via `prewarm()` /
   * `getBlobUrl()` / `eager`. Unwarmed paths return the original URL so the
   * caller's normal fetch path still runs (and logs a one-time warning).
   */
  resolveUrl(url: string): string {
    if (this.disposed) return url;
    const path = this._matchPath(url);
    if (path === null) return url;
    const obj = this.objectUrls.get(path);
    if (obj) return obj;
    // Not warmed — can't resolve synchronously. Surface a helpful hint once.
    Logger.warn(
      'Bundle',
      `resolveUrl("${url}") matched mount "${this.mount}" but path "${path}" is not warmed. ` +
        `Call prewarm([...]) or open with { eager: true } before loading.`,
    );
    return url;
  }

  /**
   * Register this bundle as a `setURLModifier` on a THREE LoadingManager so any
   * loader using that manager (GLTFLoader, FileLoader, TextureLoader, …) resolves
   * `vpak://` URLs through the bundle. Chains with any previously installed
   * modifier (yours runs first; unmatched URLs fall through to the prior one).
   *
   * Returns a disposer that restores the previous modifier.
   */
  mountToManager(manager: UrlModifierManager): () => void {
    // THREE exposes no getter for the current modifier, so we maintain our own
    // chain per manager in a module-level WeakMap. Each mount pushes a resolver
    // and (re)installs a composite that walks the chain in mount order — each
    // resolver gets a chance, the first that *changes* the URL wins, and an
    // unmatched URL falls through to identity. This lets multiple bundles share
    // one manager and lets dispose() restore the prior behavior precisely.
    const entry: UrlResolverEntry = { resolve: (url) => this.resolveUrl(url) };
    let chain = BundleLoader._managerChains.get(manager);
    if (!chain) {
      chain = [];
      BundleLoader._managerChains.set(manager, chain);
    }
    chain.push(entry);
    manager.setURLModifier(makeCompositeModifier(chain));

    let disposed = false;
    return () => {
      if (disposed) return;
      disposed = true;
      const c = BundleLoader._managerChains.get(manager);
      if (!c) return;
      const i = c.indexOf(entry);
      if (i !== -1) c.splice(i, 1);
      if (c.length === 0) {
        BundleLoader._managerChains.delete(manager);
        manager.setURLModifier((u) => u); // restore identity
      } else {
        manager.setURLModifier(makeCompositeModifier(c));
      }
    };
  }

  /**
   * Record this bundle against an AssetRegistry-like object for later lookup via
   * `BundleLoader.forRegistry(registry)`. Deliberately does NOT monkey-patch the
   * registry — it only maintains a side table, keeping the integration zero-risk.
   */
  mountToRegistry(registry: RegistryLike): () => void {
    let set = BundleLoader._registryMounts.get(registry);
    if (!set) {
      set = new Set();
      BundleLoader._registryMounts.set(registry, set);
    }
    set.add(this);
    return () => {
      const s = BundleLoader._registryMounts.get(registry);
      s?.delete(this);
      if (s && s.size === 0) BundleLoader._registryMounts.delete(registry);
    };
  }

  /** All bundles mounted to a given registry (insertion order). */
  static forRegistry(registry: RegistryLike): BundleLoader[] {
    return [...(BundleLoader._registryMounts.get(registry) ?? [])];
  }

  /**
   * Resolve a URL across every bundle mounted to `registry`. Returns the first
   * bundle's resolution that changes the URL, else the original URL. Handy as a
   * single URL modifier for a registry that aggregates several packs.
   */
  static resolveForRegistry(registry: RegistryLike, url: string): string {
    for (const b of BundleLoader.forRegistry(registry)) {
      const r = b.resolveUrl(url);
      if (r !== url) return r;
    }
    return url;
  }

  private static _registryMounts = new WeakMap<RegistryLike, Set<BundleLoader>>();

  /** Per-manager chain of URL resolvers installed via mountToManager (mount order). */
  private static _managerChains = new WeakMap<UrlModifierManager, UrlResolverEntry[]>();

  // ── Lifecycle ─────────────────────────────────────────────────────────────

  /** Revoke all object URLs and drop caches. The loader is unusable afterward. */
  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    for (const u of this.objectUrls.values()) revokeObjectURL(u);
    this.objectUrls.clear();
    this.blobCache.clear();
    this.packBytes = null;
    this.byPath.clear();
    Logger.debug('Bundle', `Disposed mount "${this.mount}"`);
  }

  // ── Internal: index loading ───────────────────────────────────────────────

  private async _loadLazy(): Promise<void> {
    // 1) Fetch the header window. If the server ignores Range (status 200), it
    //    sends the whole file — buffer it and parse from memory (fallback path).
    const headRes = await this.fetchImpl(this.url, {
      headers: { Range: `bytes=0-${HEADER_SIZE - 1}` },
    });
    if (!headRes.ok && headRes.status !== 206) {
      throw new Error(`BundleLoader: fetch failed for "${this.url}" (HTTP ${headRes.status})`);
    }
    const headBuf = new Uint8Array(await headRes.arrayBuffer());

    if (headRes.status !== 206 || headBuf.length > HEADER_SIZE) {
      // Range not honored — we got the full body (or more than the header).
      this.packBytes = headBuf;
      await this._parseFromBuffer(this.packBytes);
      Logger.debug('Bundle', `Range not supported by server; buffered whole pack (${headBuf.length} B)`);
      return;
    }

    // 2) Range honored. Parse header, then range-fetch the index region.
    const header = decodeHeader(headBuf);
    const idxBytes = await this._range(header.indexOffset, header.indexLength);
    const indexJson = (header.headerFlags & HEADER_FLAG_INDEX_BROTLI) !== 0
      ? await brotliDecompress(idxBytes, '<index>')
      : idxBytes;
    this._setIndex(parseIndexJson(indexJson));
  }

  private async _loadEager(): Promise<void> {
    const res = await this.fetchImpl(this.url);
    if (!res.ok) throw new Error(`BundleLoader: fetch failed for "${this.url}" (HTTP ${res.status})`);
    this.packBytes = new Uint8Array(await res.arrayBuffer());
    await this._parseFromBuffer(this.packBytes);
  }

  /** Parse header + index out of an already-buffered whole pack. */
  private async _parseFromBuffer(buf: Uint8Array): Promise<void> {
    const header = decodeHeader(buf.subarray(0, HEADER_SIZE));
    const idxRaw = buf.subarray(header.indexOffset, header.indexOffset + header.indexLength);
    const idxJson = (header.headerFlags & HEADER_FLAG_INDEX_BROTLI) !== 0
      ? await brotliDecompress(idxRaw, '<index>')
      : idxRaw;
    this._setIndex(parseIndexJson(idxJson));
  }

  private _setIndex(index: VpakIndex): void {
    this.index = index;
    this.byPath.clear();
    for (const e of index.entries) {
      this.byPath.set(normalizePath(e.path), e);
    }
  }

  // ── Internal: blob fetch + decode ─────────────────────────────────────────

  private async _blob(blobIndex: number, verifyOverride?: boolean): Promise<Uint8Array> {
    const cached = this.blobCache.get(blobIndex);
    if (cached) return cached;

    const verify = verifyOverride ?? this.verifyDefault;
    const meta = this.index.blobs[blobIndex];
    if (!meta) throw new Error(`BundleLoader: blob index out of range: ${blobIndex}`);

    const promise = this._fetchAndDecode(meta, verify);
    this.blobCache.set(blobIndex, promise);
    // Evict from cache on failure so a later retry can re-fetch.
    promise.catch(() => {
      if (this.blobCache.get(blobIndex) === promise) this.blobCache.delete(blobIndex);
    });
    return promise;
  }

  private async _fetchAndDecode(meta: VpakBlob, verify: boolean): Promise<Uint8Array> {
    const stored = await this._readStored(meta);
    let out: Uint8Array;
    if ((meta.flags & BLOB_FLAG_BROTLI) !== 0) {
      out = await brotliDecompress(stored, `blob@${meta.offset}`);
    } else {
      out = stored;
    }
    if (out.length !== meta.length) {
      throw new Error(
        `BundleLoader: length mismatch for blob@${meta.offset} ` +
          `(got ${out.length}, index says ${meta.length})`,
      );
    }
    if (verify) {
      const got = await sha256Hex(out);
      if (got !== meta.sha256) {
        throw new Error(
          `BundleLoader: SHA-256 mismatch for blob@${meta.offset} ` +
            `(got ${got.slice(0, 12)}…, expected ${meta.sha256.slice(0, 12)}…) — corrupt or tampered`,
        );
      }
    }
    return out;
  }

  /** Read the stored (possibly compressed) bytes for a blob. */
  private async _readStored(meta: VpakBlob): Promise<Uint8Array> {
    if (this.packBytes) {
      return this.packBytes.subarray(meta.offset, meta.offset + meta.stored);
    }
    return this._range(meta.offset, meta.stored);
  }

  /** Range-fetch `[offset, offset+length)` and return the bytes. */
  private async _range(offset: number, length: number): Promise<Uint8Array> {
    if (length === 0) return new Uint8Array(0);
    const end = offset + length - 1;
    const res = await this.fetchImpl(this.url, {
      headers: { Range: `bytes=${offset}-${end}` },
    });
    if (res.status === 206) {
      return new Uint8Array(await res.arrayBuffer());
    }
    if (res.ok) {
      // Server returned the whole file despite our Range header. Buffer it once
      // and slice — subsequent ranges will hit packBytes.
      this.packBytes = new Uint8Array(await res.arrayBuffer());
      return this.packBytes.subarray(offset, offset + length);
    }
    throw new Error(`BundleLoader: range fetch failed (HTTP ${res.status}) for "${this.url}"`);
  }

  // ── Internal: URL matching ────────────────────────────────────────────────

  /** Return the bundle-relative path a URL addresses, or null if it doesn't. */
  private _matchPath(url: string): string | null {
    // vpak://<mount>/<path>
    const scheme = 'vpak://';
    if (url.startsWith(scheme)) {
      const rest = url.slice(scheme.length);
      const slash = rest.indexOf('/');
      if (slash === -1) return null;
      const mount = rest.slice(0, slash);
      const path = normalizePath(rest.slice(slash + 1));
      if (mount !== this.mount) return null;
      return this.byPath.has(path) ? path : null;
    }
    // bare path that exists in the bundle
    const norm = normalizePath(url);
    if (this.byPath.has(norm)) return norm;
    return null;
  }

  private _assertLive(): void {
    if (this.disposed) throw new Error('BundleLoader: used after dispose()');
  }
}

// ── Module-level helpers (pure / environment-guarded) ───────────────────────

/** One resolver in a manager's modifier chain. */
interface UrlResolverEntry {
  resolve(url: string): string;
}

/**
 * Build a URL modifier that walks `chain` in order, returning the first
 * resolution that *changes* the URL, else the URL unchanged (identity). The
 * chain array is captured by reference so a later splice can't strand entries.
 */
function makeCompositeModifier(chain: readonly UrlResolverEntry[]): (url: string) => string {
  return (url: string): string => {
    for (const e of chain) {
      const r = e.resolve(url);
      if (r !== url) return r;
    }
    return url;
  };
}

function deriveMount(url: string): string {
  const clean = url.split('?')[0].split('#')[0];
  const slash = Math.max(clean.lastIndexOf('/'), clean.lastIndexOf('\\'));
  const base = slash === -1 ? clean : clean.slice(slash + 1);
  const dot = base.lastIndexOf('.');
  return dot === -1 ? base : base.slice(0, dot);
}

/**
 * Normalize to POSIX-style, strip leading "./" and "/", collapse "\\", and
 * resolve "."/".." segments.
 *
 * Why resolve ".."/".": THREE's `LoaderUtils.resolveURL` joins a glTF's base
 * dir with a relative resource URI textually, so a model at
 * `models/scene.gltf` referencing `../textures/d.png` yields
 * `vpak://assets/models/../textures/d.png`. Without collapsing the "..", the
 * bundle lookup misses and the (extremely common) relative-URI glTF breaks.
 *
 * ".." is CLAMPED at the root — segments can never escape above it. This keeps
 * traversal-style index entries ("../../secret") inside the root namespace
 * instead of letting them shadow a distinct path, which doubles as a
 * defense-in-depth guard against path traversal.
 */
function normalizePath(p: string): string {
  const s = p.replace(/\\/g, '/');
  const out: string[] = [];
  for (const seg of s.split('/')) {
    if (seg === '' || seg === '.') continue; // collapses leading "/"/"./" and "//"
    if (seg === '..') {
      out.pop(); // clamp at root: popping an empty stack is a no-op
      continue;
    }
    out.push(seg);
  }
  return out.join('/');
}

function parseIndexJson(bytes: Uint8Array): VpakIndex {
  const text = utf8Decode(bytes);
  let obj: unknown;
  try {
    obj = JSON.parse(text);
  } catch (e) {
    throw new Error(`BundleLoader: index is not valid JSON: ${(e as Error).message}`);
  }
  const idx = obj as VpakIndex;
  if (!idx || !Array.isArray(idx.blobs) || !Array.isArray(idx.entries)) {
    throw new Error('BundleLoader: index missing blobs[]/entries[]');
  }
  return idx;
}

// ── Web API shims (work in browser + Node 18+; throw clearly if absent) ─────

function utf8Decode(bytes: Uint8Array): string {
  return new TextDecoder('utf-8').decode(bytes);
}

function createObjectURL(blob: Blob): string {
  const u = (globalThis as { URL?: { createObjectURL?: (b: Blob) => string } }).URL;
  if (!u?.createObjectURL) {
    throw new Error('BundleLoader: URL.createObjectURL is unavailable in this environment');
  }
  return u.createObjectURL(blob);
}

function revokeObjectURL(url: string): void {
  const u = (globalThis as { URL?: { revokeObjectURL?: (s: string) => void } }).URL;
  u?.revokeObjectURL?.(url);
}

async function sha256Hex(bytes: Uint8Array): Promise<string> {
  const subtle = (globalThis.crypto as Crypto | undefined)?.subtle;
  if (!subtle) {
    throw new Error('BundleLoader: Web Crypto (crypto.subtle) unavailable — cannot verify SHA-256');
  }
  // digest wants a BufferSource; pass a tightly-sized copy.
  const digest = await subtle.digest('SHA-256', bytes.slice().buffer);
  const view = new Uint8Array(digest);
  let hex = '';
  for (let i = 0; i < view.length; i++) hex += view[i].toString(16).padStart(2, '0');
  return hex;
}

/**
 * Decompress brotli bytes. Preference order:
 *   1. Web API `DecompressionStream('br')` — browsers (Chrome 117+, Firefox).
 *   2. Node's `zlib.brotliDecompressSync` — server / tooling / vitest.
 * If neither is available (notably Safari, which lacks both in-page), throw a
 * clear, path-tagged error telling the caller how to recover.
 */
async function brotliDecompress(bytes: Uint8Array, label: string): Promise<Uint8Array> {
  const viaStream = await tryDecompressionStreamBr(bytes);
  if (viaStream) return viaStream;

  const viaNode = await tryNodeZlibBrotli(bytes);
  if (viaNode) return viaNode;

  throw new Error(
    `BundleLoader: cannot decompress brotli blob "${label}" — this runtime lacks brotli ` +
      `support (Safari has no in-page brotli). Cook with brotli disabled (--compress none), ` +
      `or serve the pack with HTTP 'Content-Encoding: br' so the network layer inflates it.`,
  );
}

/** Attempt browser-style `DecompressionStream('br')`. Returns null if unsupported. */
async function tryDecompressionStreamBr(bytes: Uint8Array): Promise<Uint8Array | null> {
  // The DOM lib's `CompressionFormat` predates brotli and excludes 'br', so we
  // widen the constructor's format param rather than reaching for `any`.
  const DS = (globalThis as unknown as {
    DecompressionStream?: new (format: string) => DecompressionStream;
  }).DecompressionStream;
  if (typeof DS !== 'function') return null;
  let stream: DecompressionStream;
  try {
    stream = new DS('br'); // throws in runtimes whose 'CompressionFormat' lacks 'br' (e.g. Node 22)
  } catch {
    return null;
  }
  const writer = stream.writable.getWriter();
  // copy to a standalone ArrayBuffer so we never enqueue a view into a shared buffer.
  // On corrupt brotli the writer side rejects; swallow it here (the reader side
  // below propagates the real decode error) to avoid an unhandled rejection.
  writer.write(bytes.slice()).catch(() => {});
  writer.close().catch(() => {});

  const reader = stream.readable.getReader();
  const chunks: Uint8Array[] = [];
  let total = 0;
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    if (value) {
      chunks.push(value);
      total += value.length;
    }
  }
  const out = new Uint8Array(total);
  let o = 0;
  for (const c of chunks) {
    out.set(c, o);
    o += c.length;
  }
  return out;
}

/**
 * Attempt Node's `zlib.brotliDecompressSync`. Returns null when not in Node.
 * The dynamic `import('node:zlib')` is never reached in a browser (the Node
 * detection short-circuits first), so browser bundlers can tree-shake it.
 */
async function tryNodeZlibBrotli(bytes: Uint8Array): Promise<Uint8Array | null> {
  const proc = (globalThis as { process?: { versions?: { node?: string } } }).process;
  if (!proc?.versions?.node) return null;
  try {
    const zlib = await import(/* webpackIgnore: true */ /* @vite-ignore */ 'node:zlib');
    const buf = zlib.brotliDecompressSync(bytes);
    return new Uint8Array(buf.buffer, buf.byteOffset, buf.byteLength);
  } catch {
    return null;
  }
}
